import { useMyGroups } from '@chipn/queries';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Card, EmptyState, ErrorState, PageHeader, Skeleton } from '@/components/ui';

const ROLE_LABEL = { owner: 'Owner', admin: 'Admin', member: 'Member' } as const;

export function GroupsPage() {
  const groups = useMyGroups();
  const items = groups.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <>
      <PageHeader
        title="Your groups"
        action={
          <Link to="/groups/new">
            <Button>New group</Button>
          </Link>
        }
      />
      {groups.isPending ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="space-y-2">
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-4 w-1/3" />
            </Card>
          ))}
        </div>
      ) : groups.isError ? (
        <ErrorState error={groups.error} onRetry={() => void groups.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState
          title="Create your first group"
          body="Start a group for a trip, an event, or anything you are saving for together. Have an invite link? Open it to join."
          action={
            <Link to="/groups/new">
              <Button>New group</Button>
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {items.map((g) => (
            <li key={g.id}>
              <Link to="/groups/$groupId" params={{ groupId: g.id }} className="block">
                <Card className="flex items-center justify-between gap-3 transition-colors hover:bg-surface-muted">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-text">{g.name}</p>
                    <p className="text-sm text-text-muted">
                      {g.memberCount} {g.memberCount === 1 ? 'member' : 'members'}
                      {g.archivedAt && ' · Archived'}
                    </p>
                  </div>
                  <Badge>{ROLE_LABEL[g.myRole]}</Badge>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {groups.hasNextPage && (
        <div className="mt-4">
          <Button
            variant="secondary"
            block
            loading={groups.isFetchingNextPage}
            onClick={() => void groups.fetchNextPage()}
          >
            Show more
          </Button>
        </div>
      )}
    </>
  );
}
